import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

/**
 * ProductTypeTab - Component-Based Inventory Settings
 * 
 * Allows setting product type (Standard/Raw Material/Finished Good)
 * and configuring component linking for finished goods.
 */
const ProductTypeTab = ({ product, onSave, onProductTypeChange }) => {
  const [productType, setProductType] = useState(product?.product_type || 'standard');
  const [unit, setUnit] = useState(product?.unit || 'pieces');
  const [currentStock, setCurrentStock] = useState(product?.current_stock || 0);
  const [reorderPoint, setReorderPoint] = useState(product?.reorder_point || 0);
  const [componentSku, setComponentSku] = useState(product?.component?.sku || '');
  const [qtyPerUnit, setQtyPerUnit] = useState(product?.component?.qty_per_unit || 1);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch raw materials for dropdown
  useEffect(() => {
    const fetchRawMaterials = async () => {
      try {
        const response = await api.get('/api/v1/product-master/raw-materials?tenant_id=tenant-123');
        setRawMaterials(response.raw_materials || []);
      } catch (error) {
        console.error('Error fetching raw materials:', error);
      }
    };
    fetchRawMaterials();
  }, []);

  // Initialize from product
  useEffect(() => {
    if (product) {
      setProductType(product.product_type || 'standard');
      setUnit(product.unit || 'pieces');
      setCurrentStock(product.current_stock || 0);
      setReorderPoint(product.reorder_point || 0);
      setComponentSku(product.component?.sku || '');
      setQtyPerUnit(product.component?.qty_per_unit || 1);
    }
  }, [product]);

  // Handle product type change
  const handleTypeChange = (newType) => {
    setProductType(newType);
    
    // Prepare data based on type
    const typeData = {};
    if (newType === 'raw_material') {
      typeData.unit = unit;
      typeData.current_stock = currentStock;
      typeData.reorder_point = reorderPoint;
    } else if (newType === 'finished_product') {
      typeData.component = componentSku ? {
        sku: componentSku,
        qty_per_unit: qtyPerUnit
      } : null;
    }
    
    if (onProductTypeChange) {
      onProductTypeChange(newType, typeData);
    }
  };

  // Save all settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const typeData = {};
      
      if (productType === 'raw_material') {
        typeData.unit = unit;
        typeData.current_stock = currentStock;
        typeData.reorder_point = reorderPoint;
      } else if (productType === 'finished_product') {
        if (!componentSku) {
          toast.error('Please select a raw material component');
          setSaving(false);
          return;
        }
        typeData.component = {
          sku: componentSku,
          qty_per_unit: qtyPerUnit
        };
      }
      
      if (onProductTypeChange) {
        await onProductTypeChange(productType, typeData);
      }
      
      toast.success('Product type settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Calculate available packs preview
  const getAvailabilityPreview = () => {
    if (productType !== 'finished_product' || !componentSku || qtyPerUnit <= 0) return null;
    
    const rawMaterial = rawMaterials.find(rm => rm.sku === componentSku);
    if (!rawMaterial) return null;
    
    const rawStock = rawMaterial.current_stock || 0;
    const availablePacks = Math.floor(rawStock / qtyPerUnit);
    
    return { rawStock, availablePacks, unit: rawMaterial.unit || 'pieces' };
  };

  const preview = getAvailabilityPreview();

  return (
    <div className="space-y-6">
      <h3 className="font-semibold text-lg text-gray-900 dark:text-white flex items-center gap-2">
        📦 Product Type Settings
      </h3>
      
      {/* Product Type Selector */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Product Type
        </Label>
        <div className="grid grid-cols-3 gap-3">
          {/* Standard */}
          <label 
            className={`flex flex-col items-center p-4 rounded-lg cursor-pointer border-2 transition-all ${
              productType === 'standard' 
                ? 'border-blue-500 bg-blue-500/10' 
                : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
            }`}
          >
            <input
              type="radio"
              name="product_type"
              value="standard"
              checked={productType === 'standard'}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="sr-only"
            />
            <span className="text-2xl mb-2">📦</span>
            <span className="text-white text-sm font-medium">Standard</span>
            <span className="text-gray-400 text-xs text-center mt-1">
              Track stock directly
            </span>
          </label>
          
          {/* Raw Material */}
          <label 
            className={`flex flex-col items-center p-4 rounded-lg cursor-pointer border-2 transition-all ${
              productType === 'raw_material' 
                ? 'border-orange-500 bg-orange-500/10' 
                : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
            }`}
          >
            <input
              type="radio"
              name="product_type"
              value="raw_material"
              checked={productType === 'raw_material'}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="sr-only"
            />
            <span className="text-2xl mb-2">🧱</span>
            <span className="text-white text-sm font-medium">Raw Material</span>
            <span className="text-gray-400 text-xs text-center mt-1">
              Bulk inventory in pieces
            </span>
          </label>
          
          {/* Finished Good */}
          <label 
            className={`flex flex-col items-center p-4 rounded-lg cursor-pointer border-2 transition-all ${
              productType === 'finished_product' 
                ? 'border-green-500 bg-green-500/10' 
                : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
            }`}
          >
            <input
              type="radio"
              name="product_type"
              value="finished_product"
              checked={productType === 'finished_product'}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="sr-only"
            />
            <span className="text-2xl mb-2">🎁</span>
            <span className="text-white text-sm font-medium">Finished Good</span>
            <span className="text-gray-400 text-xs text-center mt-1">
              Made from raw material
            </span>
          </label>
        </div>
      </div>
      
      {/* Raw Material Fields */}
      {productType === 'raw_material' && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2 text-orange-400 text-sm font-medium">
            <span>🧱</span>
            <span>Raw Material Settings</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm text-gray-300">Unit of Measure</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="bg-gray-700 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pieces">Pieces</SelectItem>
                  <SelectItem value="units">Units</SelectItem>
                  <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                  <SelectItem value="kg">Kilograms (kg)</SelectItem>
                  <SelectItem value="oz">Ounces (oz)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm text-gray-300">Current Stock</Label>
              <Input
                type="number"
                min="0"
                value={currentStock}
                onChange={(e) => setCurrentStock(parseInt(e.target.value) || 0)}
                className="bg-gray-700 border-gray-600"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm text-gray-300">Reorder Point</Label>
              <Input
                type="number"
                min="0"
                value={reorderPoint}
                onChange={(e) => setReorderPoint(parseInt(e.target.value) || 0)}
                className="bg-gray-700 border-gray-600"
              />
              <p className="text-xs text-gray-500">Alert when stock falls below this level</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Finished Good Fields */}
      {productType === 'finished_product' && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
            <span>🔗</span>
            <span>Link to Raw Material</span>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-gray-300">Raw Material (Component)</Label>
              <Select value={componentSku} onValueChange={setComponentSku}>
                <SelectTrigger className="bg-gray-700 border-gray-600">
                  <SelectValue placeholder="Select raw material..." />
                </SelectTrigger>
                <SelectContent>
                  {rawMaterials.length === 0 ? (
                    <div className="px-2 py-1 text-sm text-gray-400">
                      No raw materials found. Create one first.
                    </div>
                  ) : (
                    rawMaterials.map((rm) => (
                      <SelectItem key={rm.sku} value={rm.sku}>
                        {rm.sku} - {rm.name} ({rm.current_stock || 0} {rm.unit || 'pcs'})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm text-gray-300">Quantity per Unit (pieces per pack)</Label>
              <Input
                type="number"
                min="1"
                value={qtyPerUnit}
                onChange={(e) => setQtyPerUnit(parseInt(e.target.value) || 1)}
                placeholder="e.g., 8"
                className="bg-gray-700 border-gray-600"
              />
              <p className="text-xs text-gray-500">
                How many pieces of raw material are used to make one sellable unit?
              </p>
            </div>
            
            {/* Availability Preview */}
            {preview && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 mt-4">
                <p className="text-blue-300 text-sm">
                  💡 Each pack uses <strong>{qtyPerUnit}</strong> {preview.unit} of <strong>{componentSku}</strong>
                </p>
                <p className="text-blue-200 text-xs mt-1">
                  Available stock: {preview.rawStock} {preview.unit} → <strong>{preview.availablePacks} packs</strong> can be sold
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Standard Product Info */}
      {productType === 'standard' && (
        <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <span>📦</span>
            <span>Standard products track inventory directly without component linkage.</span>
          </div>
        </div>
      )}
      
      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-gray-700">
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              💾 Save Type Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProductTypeTab;
